// Newsletter data layer. Issues are editorial (admin-authored) content, so
// there is no pending/approval workflow — see migration 017. RLS lets the
// public read published issues and lets admins read/write everything, so the
// same fetchers serve both audiences: an admin simply sees drafts too.

import { supabase } from './supabase.js';

const SELECT_COLUMNS = [
  'id',
  'year',
  'month',
  'title',
  'summary',
  'body',
  'status',
  'translations',
  'published_at',
  'created_by',
  'created_at',
  'updated_at',
].join(', ');

export const NEWSLETTER_STATUSES = ['draft', 'published'];

// English is the base language (stored in title/summary/body); the others
// live in the `translations` JSONB column — see migration 018.
export const BASE_LANG = 'en';
export const TRANSLATION_LANGS = ['fr', 'es', 'de', 'it', 'pt'];
export const EMPTY_TRANSLATION = { title: '', summary: '', body: '' };

export const EMPTY_ISSUE = {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  title: 'Hermeneutics Commons News',
  summary: '',
  body: '',
  status: 'draft',
  translations: {},
};

export async function fetchIssues() {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .select(SELECT_COLUMNS)
    .order('year', { ascending: false })
    .order('month', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// The homepage teaser only needs the newest published issue.
export async function fetchLatestPublishedIssue() {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('year', { ascending: false })
    .order('month', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchIssue(year, month) {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .select(SELECT_COLUMNS)
    .eq('year', year)
    .eq('month', month)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchIssueById(id) {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function blank(v) {
  return v == null || v === '' ? null : v;
}

function normalize(form) {
  return {
    year: Number(form.year),
    month: Number(form.month),
    title: form.title?.trim(),
    summary: blank(form.summary?.trim?.()),
    body: form.body ?? '',
    status: form.status,
    translations: normalizeTranslations(form.translations),
  };
}

// Drop languages with no body so "available languages" stays meaningful,
// and never persist keys outside the supported set (the DB CHECK would
// reject them anyway).
function normalizeTranslations(translations) {
  const out = {};
  for (const lang of TRANSLATION_LANGS) {
    const t = translations?.[lang];
    if (!t || !t.body?.trim()) continue;
    out[lang] = {
      title: t.title?.trim() || '',
      summary: blank(t.summary?.trim?.()),
      body: t.body,
    };
  }
  return out;
}

export function validateIssue(form) {
  const year = Number(form.year);
  const month = Number(form.month);
  if (!Number.isInteger(year) || year < 2020 || year > 2100) return 'newsletter.form.errors.yearInvalid';
  if (!Number.isInteger(month) || month < 1 || month > 12) return 'newsletter.form.errors.monthInvalid';
  if (!form.title?.trim()) return 'newsletter.form.errors.titleRequired';
  if (!form.body?.trim()) return 'newsletter.form.errors.bodyRequired';
  if (!NEWSLETTER_STATUSES.includes(form.status)) return 'newsletter.form.errors.statusInvalid';
  return null;
}

export async function createIssue(form, { authorId }) {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .insert({ ...normalize(form), created_by: authorId })
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateIssue(id, form) {
  const { data, error } = await supabase
    .from('newsletter_issues')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteIssue(id) {
  const { error } = await supabase.from('newsletter_issues').delete().eq('id', id);
  if (error) throw error;
}

// Addresses to paste into the mail client when sending an issue manually.
// Only admins can read other profiles (RLS), so this is admin-only by
// construction. Deactivated accounts are excluded regardless of preference.
export async function fetchNewsletterRecipients() {
  const { data, error } = await supabase
    .from('profiles')
    .select('email, display_name, status')
    .eq('newsletter_opt_in', true)
    .neq('status', 'deactivated')
    .order('email');
  if (error) throw error;
  return data ?? [];
}

// ── Language helpers ──

export function availableLanguages(issue) {
  const langs = [BASE_LANG];
  for (const lang of TRANSLATION_LANGS) {
    if (issue?.translations?.[lang]?.body?.trim()) langs.push(lang);
  }
  return langs;
}

// Pick the version of an issue to show for a requested language, falling
// back to English. `lang` in the result is the language actually used, so
// callers can tell the reader when a fallback happened.
export function issueContent(issue, requestedLang) {
  const lang = (requestedLang ?? BASE_LANG).slice(0, 2);
  const t = lang !== BASE_LANG ? issue?.translations?.[lang] : null;
  if (t?.body?.trim()) {
    return {
      lang,
      title: t.title?.trim() || issue.title,
      summary: t.summary ?? issue.summary,
      body: t.body,
    };
  }
  return { lang: BASE_LANG, title: issue.title, summary: issue.summary, body: issue.body };
}

// ── URL + display helpers ──

export function issuePath(issue) {
  return `/newsletter/${issue.year}/${String(issue.month).padStart(2, '0')}`;
}

export function formatIssueMonth(year, month, locale = 'en') {
  const d = new Date(Date.UTC(year, month - 1, 1));
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function formatMonthName(month, locale = 'en') {
  const d = new Date(Date.UTC(2000, month - 1, 1));
  return d.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
}

// Parse the :year/:month route params; returns null when they're not a
// plausible period so the page can 404 instead of querying nonsense.
export function parsePeriod(yearParam, monthParam) {
  const year = Number(yearParam);
  const month = Number(monthParam);
  if (!/^\d{4}$/.test(String(yearParam ?? ''))) return null;
  if (!/^\d{1,2}$/.test(String(monthParam ?? ''))) return null;
  if (month < 1 || month > 12) return null;
  return { year, month };
}

// Given the full (already sorted newest-first) list, find the neighbours of
// one issue so the detail page can offer previous/next navigation. Only
// published issues count as neighbours — a draft shouldn't be reachable via
// "next issue" from a public page.
export function findNeighbours(issues, current) {
  const published = issues.filter((i) => i.status === 'published');
  const idx = published.findIndex((i) => i.id === current.id);
  if (idx === -1) return { newer: null, older: null };
  return {
    newer: idx > 0 ? published[idx - 1] : null,
    older: idx < published.length - 1 ? published[idx + 1] : null,
  };
}
